// src/pages/Guardias.jsx
// Calendario de guardias de sábado por turnos. La rotación la decide el
// backend (services/weekendGuardService) y la escribe en el horario de cada
// agente; aquí se ve quién cubre cada turno de cada sábado y, si eres admin o
// líder, se puede cambiar a mano o volver a tirar la rotación para un sábado.
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import api from '../services/api';
import { AuthContext } from '../context/AuthContext';


const Icon = ({ children, size = 16, stroke = 1.75 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
         stroke="currentColor" strokeWidth={stroke}
         strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
        {children}
    </svg>
);
const I = {
    refresh: <Icon size={14}><path d="M21 12a9 9 0 1 1-2.64-6.36" /><path d="M21 3v6h-6" /></Icon>,
    edit:    <Icon size={13}><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" /></Icon>,
    shield:  <Icon size={14}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z" /></Icon>,
    x:       <Icon size={12}><path d="M18 6 6 18" /><path d="m6 6 12 12" /></Icon>,
    check:   <Icon size={13}><path d="M20 6 9 17l-5-5" /></Icon>,
    office:  <Icon size={12}><path d="M3 21h18" /><path d="M5 21V7l8-4v18" /><path d="M19 21V11l-6-4" /></Icon>,
    home:    <Icon size={12}><path d="m3 11 9-8 9 8" /><path d="M5 10v11h14V10" /></Icon>,
};

const MONTHS = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
function dayMonth(iso) {
    const d = new Date(iso + 'T12:00:00');
    if (isNaN(d.getTime())) return { day: '?', month: '' };
    return { day: String(d.getDate()).padStart(2, '0'), month: MONTHS[d.getMonth()] };
}
function fmtLong(iso) {
    try {
        return new Date(iso + 'T12:00:00').toLocaleDateString('es-MX', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
    } catch { return iso; }
}
function shiftISO(iso, days) {
    const d = new Date(iso + 'T12:00:00');
    d.setDate(d.getDate() + days);
    return d.toISOString().slice(0, 10);
}
const slotName = (s) => s.label || (s.mode === 'home' ? 'Home office' : 'Oficina');


const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
.gd-root {
    --gd-accent: var(--brand-700, #0a4aa8);
    --gd-accent-hover: var(--brand-800, #07357f);
    --gd-accent-light: #dbeafe;
    --gd-accent-ring: rgba(10, 74, 168, .12);
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
    -webkit-font-smoothing: antialiased;
    max-width: 900px; margin: 0 auto; padding: 24px 20px 60px;
    color: var(--c-text-1);
}
.gd-root * { box-sizing: border-box; }
.gd-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; flex-wrap: wrap; margin-bottom: 18px; }
.gd-title { font-size: 24px; font-weight: 700; margin: 0 0 4px; letter-spacing: -0.01em; }
.gd-subtitle { color: var(--c-text-3); font-size: 13.5px; margin: 0; line-height: 1.5; max-width: 620px; }
.gd-toolbar { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; margin-bottom: 18px; }
.gd-select, .gd-input {
    padding: 8px 12px; font-size: 13.5px;
    border: 1px solid var(--c-border); border-radius: 8px;
    background: var(--c-input-bg); color: var(--c-text-1);
    font-family: inherit; outline: none;
}
.gd-select.sm { padding: 5px 8px; font-size: 12.5px; }
.gd-select:focus, .gd-input:focus { border-color: var(--gd-accent); box-shadow: 0 0 0 3px var(--gd-accent-ring); }
.gd-btn {
    display: inline-flex; align-items: center; gap: 6px;
    border: 1px solid transparent; border-radius: 8px;
    padding: 8px 14px; font-size: 13px; font-weight: 600;
    cursor: pointer; font-family: inherit; transition: all .15s;
}
.gd-btn:disabled { opacity: .5; cursor: not-allowed; }
.gd-btn.sm { padding: 5px 10px; font-size: 12px; }
.gd-btn-primary { background: var(--gd-accent); color: #fff; border-color: var(--gd-accent); }
.gd-btn-primary:hover:not(:disabled) { background: var(--gd-accent-hover); }
.gd-btn-ghost { background: transparent; color: var(--c-text-2); border-color: var(--c-border); }
.gd-btn-ghost:hover:not(:disabled) { background: var(--c-hover, rgba(15,23,42,.04)); }
.gd-check { display: inline-flex; align-items: center; gap: 6px; font-size: 13px; color: var(--c-text-3); cursor: pointer; }

.gd-list { display: flex; flex-direction: column; gap: 10px; }
.gd-card {
    background: var(--c-card); border: 1px solid var(--c-border);
    border-radius: 12px; padding: 14px 18px;
    box-shadow: 0 1px 2px rgba(15,23,42,.04);
    display: flex; gap: 16px; align-items: flex-start;
}
.gd-card.past { opacity: .6; }
.gd-card.nearest { border-color: var(--gd-accent); box-shadow: 0 0 0 3px var(--gd-accent-ring); }
.gd-date-badge {
    background: var(--gd-accent-light); color: var(--gd-accent);
    border-radius: 10px; padding: 8px 12px;
    display: flex; flex-direction: column; align-items: center;
    min-width: 64px; flex-shrink: 0;
}
.gd-date-day { font-size: 22px; font-weight: 800; line-height: 1; font-variant-numeric: tabular-nums; }
.gd-date-month { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: .5px; margin-top: 3px; }
.gd-body { flex: 1; min-width: 0; }
.gd-date-text { font-size: 12px; color: var(--c-text-4); margin: 0 0 8px; text-transform: capitalize; }
.gd-tag { display: inline-block; font-size: 10.5px; font-weight: 700; letter-spacing: .4px; text-transform: uppercase; padding: 2px 8px; border-radius: 999px; margin-left: 8px; vertical-align: middle; }
.gd-tag.next { background: var(--gd-accent); color: #fff; }
.gd-tag.manual { background: #fef3c7; color: #92400e; }
.gd-tag.skip { background: #fee2e2; color: #991b1b; margin-left: 0; }

.gd-dept-block { padding: 8px 0; border-top: 1px dashed var(--c-border); }
.gd-dept-block:first-of-type { border-top: none; padding-top: 0; }
.gd-dept-head { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; margin-bottom: 6px; }
.gd-dept { font-weight: 700; font-size: 13.5px; display: inline-flex; align-items: center; gap: 6px; }
.gd-actions { display: flex; gap: 6px; margin-left: auto; }
.gd-slots { display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: 8px; }
.gd-slot {
    border: 1px solid var(--c-border); border-radius: 10px; padding: 8px 10px;
    background: var(--c-hover, rgba(15,23,42,.02));
}
.gd-slot-head { display: flex; align-items: center; gap: 6px; font-size: 11.5px; font-weight: 700; color: var(--c-text-3); text-transform: uppercase; letter-spacing: .3px; margin-bottom: 6px; }
.gd-slot-head .gd-hours { margin-left: auto; text-transform: none; letter-spacing: 0; font-weight: 600; font-variant-numeric: tabular-nums; }
.gd-agents { display: flex; gap: 6px; flex-wrap: wrap; }
.gd-chip {
    display: inline-flex; align-items: center; gap: 6px;
    background: var(--c-card); border: 1px solid var(--c-border);
    border-radius: 999px; padding: 3px 10px; font-size: 12.5px; font-weight: 600;
}
.gd-chip.me { background: var(--gd-accent-light); color: var(--gd-accent); border-color: transparent; }
.gd-chip button { border: none; background: transparent; color: inherit; display: inline-flex; cursor: pointer; padding: 0; opacity: .7; }
.gd-chip button:hover { opacity: 1; }
.gd-chip select { border: none; background: transparent; color: inherit; font: inherit; font-size: 11px; padding: 0; cursor: pointer; max-width: 22px; opacity: .75; }
.gd-chip select:hover, .gd-chip select:focus { opacity: 1; outline: none; }
.gd-moves { margin-top: 8px; display: flex; flex-direction: column; gap: 4px; font-size: 12px; color: var(--c-text-3); }
.gd-moves span { display: inline-flex; align-items: center; gap: 6px; }
.gd-moves button { border: none; background: transparent; color: inherit; display: inline-flex; cursor: pointer; padding: 0; opacity: .7; }
.gd-none { font-size: 12px; color: var(--c-text-4); font-style: italic; }
.gd-missing { font-size: 11.5px; color: #b45309; font-weight: 600; }
.gd-editor-foot { display: flex; gap: 6px; margin-top: 8px; justify-content: flex-end; }
.gd-empty {
    text-align: center; padding: 48px 20px;
    color: var(--c-text-4); font-size: 13.5px;
    background: var(--c-card); border: 1px dashed var(--c-border); border-radius: 12px;
}
`;


export default function Guardias() {
    const { user } = useContext(AuthContext);
    const canManage = user?.role === 'admin' || user?.role === 'leader';
    const uid = String(user?.id || user?._id || '');

    const [depts,    setDepts]    = useState([]);
    const [agents,   setAgents]   = useState([]);     // agentes (para reasignar)
    const [data,     setData]     = useState({ today: '', nearestSaturday: '', guards: [] });
    const [loading,  setLoading]  = useState(true);
    const [deptId,   setDeptId]   = useState('');
    const [history,  setHistory]  = useState(false);
    const [busy,     setBusy]     = useState(false);
    const [editing,  setEditing]  = useState(null);   // { id, assignments: [{user, slot}] }

    const load = () => {
        setLoading(true);
        const params = {};
        if (deptId) params.department = deptId;
        if (history) params.from = shiftISO(new Date().toISOString().slice(0, 10), -84);
        api.get('/guards', { params })
            .then(res => setData(res.data || { guards: [] }))
            .catch(err => toast.error(err.response?.data?.error || 'No se pudieron cargar las guardias'))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        api.get('/departments').then(res => {
            let list = res.data || [];
            if (user?.role !== 'admin') {
                const mine = new Set((user?.departments || []).map(d => String(d._id || d)));
                list = list.filter(d => mine.has(String(d._id)));
            }
            setDepts(list);
        }).catch(() => {});
        if (canManage) {
            api.get('/users?role=agent').then(res => setAgents((res.data || []).filter(a => a.active !== false))).catch(() => {});
        }
    }, [user, canManage]);

    useEffect(load, [deptId, history]); // eslint-disable-line react-hooks/exhaustive-deps

    const rotatingDepts = useMemo(() => depts.filter(d => d.weekendGuard?.enabled), [depts]);

    const byDate = useMemo(() => {
        const map = new Map();
        for (const g of data.guards || []) {
            if (!map.has(g.date)) map.set(g.date, []);
            map.get(g.date).push(g);
        }
        return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0]));
    }, [data]);

    /** Turnos a mostrar para una guardia: los de la config del depto (orden y
     *  lugares) más cualquier turno "huérfano" que traiga una asignación vieja. */
    const slotsFor = (g) => {
        const cfg = (g.department?.weekendGuard?.slots || []).map(s => ({ ...s }));
        const known = new Set(cfg.map(s => s.key));
        for (const a of g.assignments || []) {
            if (!known.has(a.slot)) { known.add(a.slot); cfg.push({ key: a.slot, label: a.label, mode: a.mode, start: a.start, end: a.end, count: 0 }); }
        }
        return cfg;
    };

    const handleGenerate = async () => {
        if (!deptId && user?.role === 'leader') { toast.warn('Selecciona un departamento'); return; }
        setBusy(true);
        try {
            const res = await api.post('/guards/generate', deptId ? { department: deptId } : {});
            toast.success(res.data?.created ? `${res.data.created} sábado(s) programado(s)` : 'Todo estaba al día');
            load();
        } catch (err) {
            toast.error(err.response?.data?.error || 'No se pudo generar');
        } finally { setBusy(false); }
    };

    const handleRegenerate = async (g) => {
        if (!window.confirm(`¿Volver a tirar la rotación para el ${fmtLong(g.date)} en ${g.department?.name}?`)) return;
        setBusy(true);
        try {
            await api.post(`/guards/${g._id}/regenerate`);
            toast.success('Guardia regenerada');
            load();
        } catch (err) {
            toast.error(err.response?.data?.error || 'No se pudo regenerar');
        } finally { setBusy(false); }
    };

    const plainAssignments = (g) => (g.assignments || []).map(a => ({ user: String(a.user?._id || a.user), slot: a.slot }));
    const startEdit = (g) => setEditing({ id: g._id, assignments: plainAssignments(g), moves: [] });
    const saveEdit = async () => {
        if (!editing) return;
        setBusy(true);
        try {
            await api.patch(`/guards/${editing.id}`, { assignments: editing.assignments });
            // Movimientos a otros sábados: se agregan al destino conservando lo que ya tenía
            const byTarget = new Map();
            for (const m of editing.moves || []) {
                if (!byTarget.has(m.toShiftId)) byTarget.set(m.toShiftId, []);
                byTarget.get(m.toShiftId).push(m);
            }
            for (const [toShiftId, moves] of byTarget) {
                const target = (data.guards || []).find(g => g._id === toShiftId);
                if (!target) continue;
                const merged = plainAssignments(target);
                for (const m of moves) if (!merged.some(a => a.user === m.user && a.slot === m.toSlot)) merged.push({ user: m.user, slot: m.toSlot });
                await api.patch(`/guards/${toShiftId}`, { assignments: merged });
            }
            toast.success(byTarget.size ? 'Guardia actualizada y movimientos aplicados' : 'Guardia actualizada');
            setEditing(null);
            load();
        } catch (err) {
            toast.error(err.response?.data?.error || 'No se pudo guardar');
        } finally { setBusy(false); }
    };
    const removeFromEdit = (userId, slot) =>
        setEditing(e => ({ ...e, assignments: e.assignments.filter(a => !(a.user === userId && a.slot === slot)) }));
    /** value: 'slot:<key>' (otro turno de este sábado) o 'shift:<id>:<slotKey>' (otro sábado) */
    const moveInEdit = (userId, fromSlot, value) => {
        if (!value) return;
        if (value.startsWith('slot:')) {
            const toSlot = value.slice(5);
            setEditing(e => ({ ...e, assignments: e.assignments
                .filter(a => !(a.user === userId && a.slot === fromSlot))
                .concat(e.assignments.some(a => a.user === userId && a.slot === toSlot) ? [] : [{ user: userId, slot: toSlot }]) }));
        } else if (value.startsWith('shift:')) {
            const [, toShiftId, toSlot] = value.split(':');
            setEditing(e => ({ ...e,
                assignments: e.assignments.filter(a => !(a.user === userId && a.slot === fromSlot)),
                moves: [...(e.moves || []), { user: userId, fromSlot, toShiftId, toSlot }] }));
        }
    };
    const undoMove = (i) => setEditing(e => {
        const m = e.moves[i];
        return { ...e, moves: e.moves.filter((_, idx) => idx !== i),
            assignments: e.assignments.some(a => a.user === m.user && a.slot === m.fromSlot) ? e.assignments : [...e.assignments, { user: m.user, slot: m.fromSlot }] };
    });
    /** Otros sábados futuros del mismo depto a los que se puede mover gente. */
    const otherSaturdays = (g) => (data.guards || []).filter(x =>
        x._id !== g._id && x.date >= data.today && x.skipped !== 'holiday' &&
        String(x.department?._id || x.department) === String(g.department?._id || g.department));
    const fmtShort = (iso) => { const dm = dayMonth(iso); return `sáb ${dm.day} ${dm.month}`; };
    const addToEdit = (userId, slot) =>
        setEditing(e => e.assignments.some(a => a.user === userId && a.slot === slot) ? e : { ...e, assignments: [...e.assignments, { user: userId, slot }] });

    const agentsOfDept = (deptObj) => {
        const id = String(deptObj?._id || deptObj || '');
        return agents.filter(a => (a.departments || []).some(d => String(d._id || d) === id));
    };
    const agentName = (id) => agents.find(a => String(a._id) === String(id))?.name || '…';

    return (
        <>
            <style>{CSS}</style>
            <div className="gd-root">
                <div className="gd-head">
                    <div>
                        <h1 className="gd-title">Guardias de sábado</h1>
                        <p className="gd-subtitle">
                            Rotación automática por turnos: cada semana va quien lleva más tiempo sin guardia y recibe el turno que lleva más tiempo sin hacer.
                            El sistema escribe el turno en el horario del agente y le avisa en sesión a las 11:00.
                            {canManage && ' En cualquier sábado futuro puedes pulsar "Cambiar" para quitar, agregar o mover a alguien a otro turno u otro sábado.'}
                        </p>
                    </div>
                    {canManage && (
                        <button type="button" className="gd-btn gd-btn-primary" onClick={handleGenerate} disabled={busy}>
                            {busy ? <Spinner size="sm" animation="border" /> : <>{I.refresh} Programar lo que falte</>}
                        </button>
                    )}
                </div>

                <div className="gd-toolbar">
                    <select className="gd-select" value={deptId} onChange={e => setDeptId(e.target.value)}>
                        <option value="">Todos mis departamentos</option>
                        {depts.map(d => (
                            <option key={d._id} value={d._id}>
                                {d.name}{d.weekendGuard?.enabled ? '' : ' (sin rotación)'}
                            </option>
                        ))}
                    </select>
                    <label className="gd-check">
                        <input type="checkbox" checked={history} onChange={e => setHistory(e.target.checked)} />
                        Mostrar últimas 12 semanas
                    </label>
                </div>

                {loading ? (
                    <div style={{ textAlign: 'center', padding: 60 }}><Spinner animation="border" variant="primary" /></div>
                ) : byDate.length === 0 ? (
                    <div className="gd-empty">
                        {rotatingDepts.length === 0
                            ? <>Ningún departamento tiene activada la rotación de guardias.{canManage && <> Actívala en <b>Administración → Departamentos → Editar → Guardias de sábado</b>.</>}</>
                            : 'No hay guardias programadas para este rango.'}
                    </div>
                ) : (
                    <div className="gd-list">
                        {byDate.map(([date, rows]) => {
                            const dm = dayMonth(date);
                            const isPast = date < data.today;
                            const isNearest = date === data.nearestSaturday;
                            return (
                                <div key={date} className={`gd-card${isPast ? ' past' : ''}${isNearest ? ' nearest' : ''}`}>
                                    <div className="gd-date-badge">
                                        <span className="gd-date-day">{dm.day}</span>
                                        <span className="gd-date-month">{dm.month}</span>
                                    </div>
                                    <div className="gd-body">
                                        <p className="gd-date-text">
                                            {fmtLong(date)}
                                            {isNearest && <span className="gd-tag next">Este sábado</span>}
                                        </p>
                                        {rows.map(g => {
                                            const isEditing = editing?.id === g._id;
                                            const slots = slotsFor(g);
                                            const pool = isEditing ? agentsOfDept(g.department) : [];
                                            const current = isEditing ? editing.assignments : (g.assignments || []).map(a => ({ user: String(a.user?._id || a.user), slot: a.slot, name: a.user?.name, email: a.user?.email }));
                                            return (
                                                <div key={g._id} className="gd-dept-block">
                                                    <div className="gd-dept-head">
                                                        <span className="gd-dept">{I.shield} {g.department?.name || '—'}</span>
                                                        {g.skipped === 'holiday' && <span className="gd-tag skip">Feriado · sin guardia</span>}
                                                        {g.skipped === 'no-agents' && !isEditing && <span className="gd-tag skip">Sin agentes elegibles</span>}
                                                        {g.source === 'manual' && <span className="gd-tag manual" title={g.changedBy?.name ? `Cambiado por ${g.changedBy.name}` : ''}>Manual</span>}
                                                        {canManage && !isPast && !isEditing && g.skipped !== 'holiday' && (
                                                            <div className="gd-actions">
                                                                <button type="button" className="gd-btn gd-btn-ghost sm" onClick={() => startEdit(g)} disabled={busy}>{I.edit} Cambiar</button>
                                                                <button type="button" className="gd-btn gd-btn-ghost sm" onClick={() => handleRegenerate(g)} disabled={busy} title="Vuelve a tirar la rotación (útil tras cargar vacaciones)">{I.refresh}</button>
                                                            </div>
                                                        )}
                                                    </div>
                                                    {g.skipped !== 'holiday' && (
                                                        <div className="gd-slots">
                                                            {slots.map(s => {
                                                                const inSlot = current.filter(a => a.slot === s.key);
                                                                const faltan = Math.max(0, (Number(s.count) || 0) - inSlot.length);
                                                                return (
                                                                    <div key={s.key} className="gd-slot">
                                                                        <div className="gd-slot-head">
                                                                            {s.mode === 'home' ? I.home : I.office} {slotName(s)}
                                                                            <span className="gd-hours">{s.start}–{s.end}</span>
                                                                        </div>
                                                                        <div className="gd-agents">
                                                                            {inSlot.length === 0 && !faltan && <span className="gd-none">—</span>}
                                                                            {inSlot.map(a => (
                                                                                <span key={a.user} className={`gd-chip${a.user === uid ? ' me' : ''}`} title={a.email || ''}>
                                                                                    {isEditing ? agentName(a.user) : a.name}{a.user === uid ? ' · tú' : ''}
                                                                                    {isEditing && (
                                                                                        <select value="" title="Mover a…" aria-label="Mover a" onChange={e => moveInEdit(a.user, s.key, e.target.value)}>
                                                                                            <option value="">⇄</option>
                                                                                            <optgroup label="Este sábado, otro turno">
                                                                                                {slots.filter(o => o.key !== s.key && Number(o.count) > 0).map(o => (
                                                                                                    <option key={o.key} value={`slot:${o.key}`}>{slotName(o)} {o.start}–{o.end}</option>
                                                                                                ))}
                                                                                            </optgroup>
                                                                                            <optgroup label="Otro sábado (mismo turno)">
                                                                                                {otherSaturdays(g).map(x => {
                                                                                                    const xs = slotsFor(x);
                                                                                                    const same = xs.find(o => o.key === s.key) || xs[0];
                                                                                                    return same ? <option key={x._id} value={`shift:${x._id}:${same.key}`}>{fmtShort(x.date)} · {slotName(same)} {same.start}–{same.end}</option> : null;
                                                                                                })}
                                                                                            </optgroup>
                                                                                        </select>
                                                                                    )}
                                                                                    {isEditing && <button type="button" aria-label="Quitar" onClick={() => removeFromEdit(a.user, s.key)}>{I.x}</button>}
                                                                                </span>
                                                                            ))}
                                                                            {faltan > 0 && !isEditing && <span className="gd-missing">{faltan} lugar{faltan === 1 ? '' : 'es'} sin cubrir</span>}
                                                                            {isEditing && (
                                                                                <select className="gd-select sm" value="" onChange={e => { if (e.target.value) addToEdit(e.target.value, s.key); }}>
                                                                                    <option value="">{faltan > 0 ? `Agregar (faltan ${faltan})…` : 'Agregar…'}</option>
                                                                                    {pool.filter(a => !inSlot.some(x => x.user === String(a._id))).map(a => (
                                                                                        <option key={a._id} value={String(a._id)}>{a.name}</option>
                                                                                    ))}
                                                                                </select>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    )}
                                                    {isEditing && (editing.moves || []).length > 0 && (
                                                        <div className="gd-moves">
                                                            {editing.moves.map((m, i) => {
                                                                const tgt = (data.guards || []).find(x => x._id === m.toShiftId);
                                                                const ts = tgt ? slotsFor(tgt).find(o => o.key === m.toSlot) : null;
                                                                return (
                                                                    <span key={i}>
                                                                        {I.refresh} <b>{agentName(m.user)}</b> se mueve al {tgt ? fmtShort(tgt.date) : '…'}{ts ? ` · ${slotName(ts)} ${ts.start}–${ts.end}` : ''}
                                                                        <button type="button" aria-label="Deshacer" onClick={() => undoMove(i)}>{I.x}</button>
                                                                    </span>
                                                                );
                                                            })}
                                                        </div>
                                                    )}
                                                    {isEditing && (
                                                        <div className="gd-editor-foot">
                                                            <button type="button" className="gd-btn gd-btn-ghost sm" onClick={() => setEditing(null)} disabled={busy}>Cancelar</button>
                                                            <button type="button" className="gd-btn gd-btn-primary sm" onClick={saveEdit} disabled={busy}>{I.check} Guardar</button>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </>
    );
}
